// =============================================================================
// Shared by every price list screen: API address, file import / export, prices.
// The API address comes from config/api.js (never localhost on the live site).
// =============================================================================
import * as XLSX from 'xlsx';
import { SMIIFY_API } from '../../config/api';

export const SERVICES_API = `${SMIIFY_API}/services`;

/** fetch with login + branch headers; returns json, throws Error(message) */
export const servicesFetch = async (path = '', { method = 'GET', body, storeId } = {}) => {
    const sid = storeId || localStorage.getItem('smiify_active_store_id') || '';
    const res = await fetch(`${SERVICES_API}${path}`, {
        method,
        headers: {
            Authorization: `Bearer ${localStorage.getItem('smiify_token')}`,
            ...(sid ? { 'x-store-id': String(sid) } : {}),
            ...(body ? { 'Content-Type': 'application/json' } : {}),
        },
        body: body ? JSON.stringify(body) : undefined,
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok && !json?.data) throw new Error(json.message || `Request failed (${res.status}).`);
    return { ...json, httpStatus: res.status };
};
export const errorText = (e) => (e?.message === 'Failed to fetch' ? 'Cannot reach the server. Check your internet and try again.' : e?.message || 'Something went wrong.');

export const loadServices = async (storeId) => {
    const json = await servicesFetch(`?storeId=${encodeURIComponent(storeId)}`, { storeId });
    if (!json.success) throw new Error(json.message || 'Unable to load the price list.');
    return (json.data || []).map((s) => ({ ...s, id: s.id || s._id }));
};

/** Saves many services in one request: [{ id?, name, targetDemographic, category, duration, price, discount, addons? }] */
export const saveServicesBulk = async (storeId, services) => servicesFetch('/bulk', { method: 'POST', storeId, body: { storeId, services } });

/* ---------------------------------------------------------------- values */
export const toNumber = (value, fallback = 0) => {
    const n = Number(String(value ?? '').replace(/[₹,]/g, '').trim());
    return Number.isFinite(n) ? n : fallback;
};
export const clean = (v) => String(v ?? '').replace(/\s+/g, ' ').trim();

/** Audience + Category + Name: "Haircut" for Men and for Women are two services */
export const serviceKey = (audience, category, name) => [audience, category, name].map((v) => clean(v).toLowerCase()).join('|');

export const effectivePrice = (row) => {
    const base = Math.max(0, toNumber(row.price));
    if (!row.discountActive || row.discountValue === '') return base;
    const off = Math.max(0, toNumber(row.discountValue));
    return Math.max(0, row.discountType === 'Flat' ? base - off : base - (base * off) / 100);
};
export const inr = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
export const newRowId = (prefix = 'row') => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

/* ---------------------------------------------------------------- import */
// "Service Duration (mins)", "Price (Rs)", "Price ₹" -> "service duration", "price": units at the end are ignored
const normaliseHeader = (v) => String(v || '')
    .replace(/^\uFEFF/, '').trim().toLowerCase()
    .replace(/[()[\]_.\-:*]/g, ' ').replace(/₹/g, ' rs ').replace(/\s+/g, ' ').trim()
    .replace(/(\s(mins?|minutes?|rs|inr|rupees?))+$/, '')
    .trim();

/** Value of the first column whose heading matches one of the names */
export const readColumn = (row, aliases) => {
    const wanted = aliases.map(normaliseHeader);
    const entry = Object.entries(row).find(([k]) => wanted.includes(normaliseHeader(k)));
    return entry ? entry[1] : '';
};

export const discountTypeOf = (v) => {
    const t = String(v || '').trim().toLowerCase();
    return t.includes('flat') || t.includes('amount') || t.includes('₹') ? 'Flat' : 'Percentage';
};
export const isYes = (v) => ['true', 'yes', '1', 'active', 'on', 'y'].includes(String(v ?? '').trim().toLowerCase());

/** Reads the first sheet of an Excel file, or a CSV, into [{ heading: value }] */
export const readSheetFile = async (file) => {
    if (!file) return [];
    if (!/\.(xlsx|xls|csv)$/i.test(file.name)) throw new Error('Choose an Excel (.xlsx, .xls) or CSV file.');
    const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });   // xlsx reads CSV too (quotes, commas, BOM)
    return XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: '' });
};

/* ---------------------------------------------------------------- export */
export const writeSheet = (rows, fileName, sheetName = 'Sheet1') => {
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), sheetName);
    XLSX.writeFile(wb, fileName);
};
export const writeCsv = (rows, fileName) => {
    const ws = XLSX.utils.json_to_sheet(rows);
    const csv = XLSX.utils.sheet_to_csv(ws);
    const url = URL.createObjectURL(new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url; a.download = fileName;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
};
